import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Single-line refined design:
// POST · VFX · MADNESS — clean letter cascade reveal, subtle VFX breathe,
// quiet pulsing diamonds, a thin red signature line that draws in beneath.
export const Statement = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });
    const ghostX = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
    const ghostOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.04, 0.1, 0.04]);

    // Scroll-linked transforms for the "Welcome to the Mad House" overline
    const welcomeTracking = useTransform(scrollYProgress, [0, 0.5, 1], ["0.1em", "0.55em", "0.9em"]);
    const welcomeX = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
    const welcomeOpacity = useTransform(scrollYProgress, [0, 0.15, 0.75, 1], [0, 1, 1, 0.35]);
    const lineScaleX = useTransform(scrollYProgress, [0, 0.4, 0.9], [0, 1, 1]);
    const lineOpacity = useTransform(scrollYProgress, [0, 0.2, 0.85, 1], [0, 1, 1, 0.4]);
    const tickY = useTransform(scrollYProgress, [0, 1], ["0px", "-4px"]);

    const Cascade = ({ text, className = "" }) => (
        <span className={`inline-flex whitespace-nowrap ${className}`}>
            {Array.from(text).map((ch, i) => (
                <motion.span
                    key={`${text}-${i}`}
                    initial={{ opacity: 0, y: "55%" }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{
                        duration: 0.85,
                        delay: i * 0.045,
                        ease: [0.22, 1, 0.36, 1]
                    }}
                    className="inline-block"
                >
                    <motion.span
                        animate={{
                            y: [0, -6, 3, -2, 0],
                            rotate: [0, -1.5, 1, -1, 0]
                        }}
                        transition={{
                            duration: 3.6 + (i % 3) * 0.3,
                            delay: 0.8 + i * 0.08,
                            repeat: Infinity,
                            repeatType: "mirror",
                            ease: "easeInOut"
                        }}
                        whileHover={{ y: -12, scale: 1.08 }}
                        className="inline-block origin-bottom"
                    >
                        {ch}
                    </motion.span>
                </motion.span>
            ))}
            <span className="text-[#DB162F]">.</span>
        </span>
    );

    const Diamond = ({ delay }) => (
        <motion.span
            aria-hidden
            initial={{ scale: 0, rotate: 0, opacity: 0 }}
            whileInView={{ scale: 1, rotate: 45, opacity: 1 }}
            viewport={{ once: true, margin: "-20%" }}
            transition={{ duration: 0.6, delay }}
            className="hidden self-center md:block"
        >
            <motion.span
                animate={{ scale: [1, 1.3, 1], opacity: [0.85, 1, 0.85] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="block h-[0.18em] w-[0.18em] bg-[#DB162F]"
            />
        </motion.span>
    );

    return (
        <section
            id="studio"
            ref={ref}
            data-testid="statement-section"
            className="relative isolate overflow-hidden border-y border-ink-border px-6 py-24 md:px-12 md:py-32"
        >
            <div
                data-testid="statement-text"
                className="relative z-10 mx-auto flex max-w-[1800px] flex-col items-center gap-8 md:gap-12"
            >
                {/* HEADLINE — POST · VFX. · MADNESS. */}
                <div
                    className="flex w-full flex-nowrap items-baseline justify-center gap-x-2 md:gap-x-4 lg:gap-x-6"
                    style={{
                        fontSize: "clamp(1.5rem, 5.8vw, 7rem)",
                        lineHeight: 0.85,
                        letterSpacing: "-0.05em"
                    }}
                >
                    <span className="font-display uppercase text-fog-primary">
                        <Cascade text="POST" />
                    </span>

                    <Diamond delay={0.15} />

                    {/* VFX — subtle breathe + gentle wobble */}
                    <motion.span
                        initial={{ opacity: 0, y: 30, filter: "blur(8px)", rotate: -8 }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)", rotate: -5 }}
                        viewport={{ once: true, margin: "-20%" }}
                        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="inline-block"
                    >
                        <motion.span
                            animate={{
                                scale: [1, 1.03, 1],
                                rotate: [-5, -7, -5]
                            }}
                            transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
                            whileHover={{ scale: 1.1, rotate: -10 }}
                            className="inline-block font-display italic uppercase text-[#DB162F]"
                            style={{
                                fontSize: "1.15em",
                                textShadow:
                                    "0 0 50px rgba(219, 22, 47, 0.4), 0 0 22px rgba(219, 22, 47, 0.25)"
                            }}
                        >
                            VFX.
                        </motion.span>
                    </motion.span>

                    <Diamond delay={0.3} />

                    <span className="font-display uppercase text-fog-primary">
                        <Cascade text="MADNESS" />
                    </span>
                </div>

                {/* TAGLINE — WELCOME TO THE MAD HOUSE — small, one line */}
                <motion.div
                    style={{ opacity: welcomeOpacity, x: welcomeX }}
                    className="flex flex-col items-center gap-3"
                >
                    <motion.div
                        style={{ letterSpacing: welcomeTracking }}
                        className="flex flex-nowrap items-center justify-center whitespace-nowrap font-mono text-[10px] uppercase text-fog-primary md:text-xs"
                    >
                        {Array.from("WELCOME TO THE MAD HOUSE").map((ch, i) => (
                            <motion.span
                                key={`w-${i}`}
                                initial={{ opacity: 0, y: 12, rotateX: -70 }}
                                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                                viewport={{ once: true, margin: "-15%" }}
                                transition={{
                                    duration: 0.6,
                                    delay: i * 0.025,
                                    ease: [0.22, 1, 0.36, 1]
                                }}
                                className="inline-block"
                                style={{ transformStyle: "preserve-3d" }}
                            >
                                {ch === " " ? "\u00A0\u00A0" : ch}
                            </motion.span>
                        ))}
                    </motion.div>

                    {/* Subtle red underline */}
                    <motion.div
                        style={{ scaleX: lineScaleX, opacity: lineOpacity }}
                        className="h-px w-24 origin-center bg-[#DB162F]/60 md:w-40"
                    />
                </motion.div>

            </div>
        </section>
    );
};

export default Statement;
